(() => {
  const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  const range = value => {
    const matches = String(value).replace(/,/g, '.').match(/\d+(?:\.\d+)?/g);
    if (!matches || matches.length > 2) return null;
    const values = matches.map(Number);
    const min = values[0], max = values[1] ?? min;
    return min > 0 && max >= min ? {min, max, mid: (min + max) / 2, text: value.replace(/\s*cm\b/gi, '').trim()} : null;
  };
  const number = value => /^\d{1,3}(?:[.,]\d{1,2})?$/.test(value.trim()) ? Number(value.replace(',', '.')) : NaN;
  function choose(rows, width, length, preference, bottom) {
    const adjustment = preference === 'loose' ? (bottom ? 1 : 2) : preference === 'fitted' ? (bottom ? -1 : -2) : 0;
    const target = width + adjustment;
    const min = Math.min(...rows.map(r => r.width.min)) - 2;
    const max = Math.max(...rows.map(r => r.width.max)) + 2;
    if (target < min || target > max) return {outside: true};
    const ranked = rows.map(row => ({
      ...row,
      score: Math.abs(row.width.mid - target) + (length && row.length ? Math.abs(row.length.mid - length) * .22 : 0)
    })).sort((a, b) => a.score - b.score);
    const best = ranked[0];
    if (Math.abs(best.width.mid - target) > 5 || (length && best.length && Math.abs(best.length.mid - length) > 12)) return {outside: true};
    return {best, alternative: ranked[1] && ranked[1].score - best.score <= 1.2 ? ranked[1] : null};
  }
  class PanoFittingRoom extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.data = JSON.parse(this.querySelector('[data-fit-data]').textContent);
        this.text = JSON.parse(this.querySelector('[data-fit-text]').textContent);
        this.scope = this.closest('.product-detail');
        this.dialog = this.querySelector('dialog');
        this.picker = this.scope?.querySelector('.product-variant');
        if (!this.picker || !this.dialog.showModal) return;
        this.sizeIndex = this.data.options.findIndex(o => /tamanho|size|numeracao/.test(normalize(o)));
        if (this.sizeIndex < 0) return;
        const table = this.querySelector('[data-fit-table]').content.querySelector('table');
        if (!table) return;
        const head = Array.from(table.querySelectorAll('tr')[0].children).map(c => normalize(c.textContent));
        const widthIndex = head.findIndex(h => /largura|axila/.test(h));
        const lengthIndex = head.findIndex(h => /comprimento|altura/.test(h));
        if (widthIndex < 1) return;
        this.bottom = /cos|cintura/.test(head[widthIndex]);
        const sizeNames = new Set(this.data.variants.map(v => normalize(v.options[this.sizeIndex])));
        this.rows = Array.from(table.querySelectorAll('tr')).slice(1).map(tr => {
          const cells = Array.from(tr.children).map(c => c.textContent.trim());
          return {size: cells[0], width: range(cells[widthIndex]), length: lengthIndex > -1 ? range(cells[lengthIndex]) : null};
        }).filter(r => r.width && sizeNames.has(normalize(r.size)));
        if (!this.rows.length) return;
        this.querySelector('[data-fit-width-label]').textContent = this.text[this.bottom ? 'width_bottom' : 'width_top'];
        this.querySelector('[data-fit-instruction]').textContent = this.text[this.bottom ? 'instruction_bottom' : 'instruction_top'];
        this.querySelector('.pn-fit__trigger').addEventListener('click', () => {
          this.opener = document.activeElement;
          this.dialog.showModal();
          this.updateAvailability();
        });
        this.querySelector('.pn-fit__close').addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
        });
        this.dialog.addEventListener('close', () => this.opener?.focus());
        this.querySelector('.pn-fit__calculate').addEventListener('click', () => this.calculate());
        this.querySelector('.pn-fit__apply').addEventListener('click', () => this.apply());
        this.dialog.addEventListener('keydown', event => {
          if (event.key === 'Enter' && event.target.matches('input')) {event.preventDefault(); this.calculate();}
        });
        this.dialog.querySelectorAll('input,select').forEach(el => el.addEventListener('input', () => {
          this.best = null;
          this.querySelector('.pn-fit__result').hidden = true;
          this.querySelector('.pn-fit__error').hidden = true;
        }));
        this.picker.addEventListener('change', () => this.updateAvailability());
        this.hidden = false;
      } catch (_) {
        this.hidden = true;
      }
    }
    error(message) {
      const error = this.querySelector('.pn-fit__error');
      error.textContent = message;
      error.hidden = false;
      this.querySelector('.pn-fit__result').hidden = true;
    }
    calculate() {
      const width = number(this.querySelector('[data-fit-width]').value);
      const rawLength = this.querySelector('[data-fit-length]').value.trim();
      const length = rawLength ? number(rawLength) : null;
      this.best = null;
      if (!Number.isFinite(width) || width < 15 || width > 120 || (rawLength && (!Number.isFinite(length) || length < 10 || length > 160))) {
        this.error(this.text.invalid);
        return;
      }
      const result = choose(this.rows, width, length, this.querySelector('[data-fit-preference]').value, this.bottom);
      if (result.outside) {this.error(this.text.outside); return;}
      this.best = result.best;
      this.querySelector('.pn-fit__error').hidden = true;
      this.querySelector('.pn-fit__size').textContent = this.best.size;
      this.querySelector('.pn-fit__match').textContent = (length ? this.text.matched : this.text.matched_width) + ' ' + this.text.range.replace('{range}', this.best.width.text) + (result.alternative ? ' ' + this.text.between.replace('{size}', result.alternative.size) : '');
      this.querySelector('.pn-fit__result').hidden = false;
      this.updateAvailability();
      this.querySelector('.pn-fit__result').scrollIntoView({block:'nearest'});
    }
    selection() {
      return this.data.options.map((_, index) => {
        const group = this.picker.querySelector('[data-option-index="' + (index + 1) + '"]');
        return group?.querySelector('input:checked, select')?.value;
      });
    }
    matchingVariant() {
      if (!this.best) return null;
      const selected = this.selection();
      return this.data.variants.find(v => v.options.every((value, index) => normalize(value) === normalize(index === this.sizeIndex ? this.best.size : selected[index])));
    }
    updateAvailability() {
      if (!this.best) return;
      const variant = this.matchingVariant();
      const status = this.querySelector('.pn-fit__availability');
      status.textContent = !variant ? this.text.no_variant : variant.available ? this.text.available : this.text.unavailable;
      const group = this.picker.querySelector('[data-option-index="' + (this.sizeIndex + 1) + '"]');
      const control = group && Array.from(group.querySelectorAll('input, option')).find(el => normalize(el.value) === normalize(this.best.size));
      this.querySelector('.pn-fit__apply').hidden = !variant?.available || !control || control.disabled;
    }
    apply() {
      if (!this.best || !this.matchingVariant()?.available) {this.updateAvailability(); return;}
      const group = this.picker.querySelector('[data-option-index="' + (this.sizeIndex + 1) + '"]');
      const radio = group && Array.from(group.querySelectorAll('input[type="radio"]')).find(el => normalize(el.value) === normalize(this.best.size));
      const select = group?.querySelector('select');
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', {bubbles: true}));
      } else if (select) {
        const option = Array.from(select.options).find(el => normalize(el.value) === normalize(this.best.size));
        if (!option || option.disabled) return;
        select.value = option.value;
        select.dispatchEvent(new Event('change', {bubbles: true}));
      } else {
        this.querySelector('.pn-fit__availability').textContent = this.text.pick_manually;
        return;
      }
      this.dialog.close();
      (radio?.labels?.[0] || select || group).scrollIntoView({block: 'center'});
    }
  }
  if (!customElements.get('pano-fitting-room')) customElements.define('pano-fitting-room', PanoFittingRoom);
})();
